import { FormEvent, useEffect, useLayoutEffect, useRef, useState } from "react";
import { branding } from "../config/branding";
import { ModelMode } from "../config/modelConfig";
import { Workflow } from "../config/workflowConfig";
import { TroubleshootingOption } from "../config/troubleshootingConfig";
import { useChatSend } from "../hooks/useChatSend";
import { useConversations } from "../hooks/useConversations";
import { useSettings } from "../hooks/useSettings";
import { CustomerContextInput } from "./CustomerContextInput";
import { AttachmentPreview, PendingAttachment } from "./AttachmentPreview";
import { ImageAttachButton } from "./ImageAttachButton";
import { LoadingIndicator } from "./LoadingIndicator";
import { MessageBubble } from "./MessageBubble";
import { SendIcon, HidLogo } from "./Icons";
import { SettingsPanel } from "./SettingsPanel";
import { Sidebar } from "./Sidebar";
import { TroubleshootingOptions } from "./TroubleshootingOptions";
import { WelcomePanel } from "./WelcomePanel";
import { WorkflowSelector } from "./WorkflowSelector";
import { SessionConclusionDialog } from "./SessionConclusionDialog";
import { MODEL_CONFIG } from "../config/modelConfig";
import { uploadImage, SessionConclusionResponse } from "../services/api";
import { ChatImageRef } from "../types/chat";

interface ChatWindowProps {
  onOpenAdmin?: () => void;
}

export function ChatWindow({ onOpenAdmin }: ChatWindowProps) {
  const {
    modelMode,
    setModelMode,
    settingsOpen,
    setSettingsOpen,
    fallbackNotice,
    applyBackendNotice,
    modelConfig,
  } = useSettings();

  const {
    conversations,
    activeConversation,
    activeId,
    startNewChat,
    selectConversation,
    deleteConversation,
    updateActiveMeta,
    appendUserMessage,
    appendMessages,
  } = useConversations(modelMode);

  const { isLoading, error, setError, sendMessage } = useChatSend();

  const [query, setQuery] = useState("");
  const [workflow, setWorkflow] = useState<Workflow>(activeConversation.selectedWorkflow);
  const [troubleshooting, setTroubleshooting] = useState<TroubleshootingOption[]>(
    activeConversation.selectedTroubleshootingOptions
  );
  const [customerName, setCustomerName] = useState(activeConversation.customerName ?? "");
  const [caseId, setCaseId] = useState(activeConversation.caseId ?? "");
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [sessionConclusionOpen, setSessionConclusionOpen] = useState(false);
  const [conclusionResult, setConclusionResult] = useState<SessionConclusionResponse | null>(null);

  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Sync local form state when switching conversations
  useEffect(() => {
    setWorkflow(activeConversation.selectedWorkflow);
    setTroubleshooting(activeConversation.selectedTroubleshootingOptions);
    setCustomerName(activeConversation.customerName ?? "");
    setCaseId(activeConversation.caseId ?? "");
    setError(null);
  }, [activeId, activeConversation, setError]);

  useLayoutEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
  }, [activeConversation.messages, isLoading]);

  const persistMeta = (
    w: Workflow,
    t: TroubleshootingOption[],
    cust: string,
    cId: string,
    mode: ModelMode = modelMode
  ) => {
    updateActiveMeta({
      selectedWorkflow: w,
      selectedTroubleshootingOptions: t,
      customerName: cust.trim() || undefined,
      caseId: cId.trim() || undefined,
      selectedModelMode: mode,
    });
  };

  const handleWorkflowChange = (w: Workflow) => {
    setWorkflow(w);
    persistMeta(w, troubleshooting, customerName, caseId);
  };

  const handleTroubleshootingChange = (t: TroubleshootingOption[]) => {
    setTroubleshooting(t);
    persistMeta(workflow, t, customerName, caseId);
  };

  const handleCustomerChange = (v: string) => {
    setCustomerName(v);
    persistMeta(workflow, troubleshooting, v, caseId);
  };

  const handleCaseChange = (v: string) => {
    setCaseId(v);
    persistMeta(workflow, troubleshooting, customerName, v);
  };

  const clearAttachments = () => {
    setAttachments((prev) => {
      prev.forEach((a) => URL.revokeObjectURL(a.previewUrl));
      return [];
    });
  };

  const handleAttachSelect = async (files: File[]) => {
    const queued: PendingAttachment[] = files.map((file) => ({
      localId: `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      previewUrl: URL.createObjectURL(file),
      fileName: file.name,
      status: "uploading",
    }));

    setAttachments((prev) => [...prev, ...queued]);

    await Promise.all(
      queued.map(async (item, i) => {
        try {
          const res = await uploadImage(files[i]);
          setAttachments((prev) =>
            prev.map((a) =>
              a.localId === item.localId
                ? { ...a, status: "ready", uploadId: res.id }
                : a
            )
          );
        } catch (err: unknown) {
          const detail =
            err && typeof err === "object" && "response" in err
              ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
              : undefined;
          setAttachments((prev) =>
            prev.map((a) =>
              a.localId === item.localId
                ? { ...a, status: "error", error: detail || "Upload failed" }
                : a
            )
          );
        }
      })
    );
  };

  const handleAttachRemove = (localId: string) => {
    setAttachments((prev) => {
      const target = prev.find((a) => a.localId === localId);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((a) => a.localId !== localId);
    });
  };

  const handleNewChat = () => {
    startNewChat(modelMode, workflow);
    setQuery("");
    clearAttachments();
    setError(null);
  };

  const uploading = attachments.some((a) => a.status === "uploading");

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = query.trim();
    if (!trimmed || isLoading || uploading) return;

    persistMeta(workflow, troubleshooting, customerName, caseId);

    const images: ChatImageRef[] = attachments
      .filter((a) => a.status === "ready" && a.uploadId)
      .map((a) => ({ upload_id: a.uploadId as string }));

    await sendMessage({
      message: trimmed,
      conversation: {
        ...activeConversation,
        customerName: customerName.trim() || undefined,
        caseId: caseId.trim() || undefined,
        selectedWorkflow: workflow,
        selectedTroubleshootingOptions: troubleshooting,
        selectedModelMode: modelMode,
      },
      modelMode,
      workflow,
      troubleshootingOptions: troubleshooting,
      caseId: caseId.trim() || undefined,
      images: images.length > 0 ? images : undefined,
      onFallbackNotice: applyBackendNotice,
      appendUserMessage,
      appendMessages,
    });
    setQuery("");
    clearAttachments();
  };

  const messages = activeConversation.messages;

  return (
    <div className="flex h-screen bg-chat-bg">
      <Sidebar
        conversations={conversations}
        activeId={activeId}
        onNewChat={handleNewChat}
        onSelectConversation={selectConversation}
        onDeleteConversation={deleteConversation}
        onOpenSettings={() => setSettingsOpen(true)}
        onOpenAdmin={onOpenAdmin}
        onExampleClick={setQuery}
        disabled={isLoading}
      />

      <SettingsPanel
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        modelMode={modelMode}
        onModelModeChange={(m) => {
          setModelMode(m);
          persistMeta(workflow, troubleshooting, customerName, caseId, m);
        }}
        modelConfig={modelConfig}
        fallbackNotice={fallbackNotice}
        onOpenAdmin={onOpenAdmin}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 bg-white/80 px-6 py-3 backdrop-blur">
          <div className="flex items-center gap-4">
            <HidLogo className="h-8 w-auto max-w-[120px]" />
            <div>
              <h2 className="text-sm font-bold text-brand-navy">{branding.appTitle}</h2>
              <p className="text-xs text-gray-500">
                {MODEL_CONFIG[modelMode].label} · {MODEL_CONFIG[modelMode].displayModel}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {messages.length > 0 && (
              <button
                onClick={() => setSessionConclusionOpen(true)}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition border border-emerald-200"
              >
                End Session
              </button>
            )}
          </div>
        </header>

        <div className="border-b border-gray-100 bg-white px-6 py-4">
          <div className="mx-auto max-w-3xl space-y-4">
            <CustomerContextInput
              customerName={customerName}
              caseId={caseId}
              onCustomerNameChange={handleCustomerChange}
              onCaseIdChange={handleCaseChange}
              disabled={isLoading}
            />
            <WorkflowSelector value={workflow} onChange={handleWorkflowChange} disabled={isLoading} />
            <TroubleshootingOptions
              selected={troubleshooting}
              onChange={handleTroubleshootingChange}
              disabled={isLoading}
            />
          </div>
        </div>

        {fallbackNotice && (
          <div className="border-b border-amber-100 bg-amber-50 px-6 py-2 text-center text-xs text-amber-800">
            {fallbackNotice}
          </div>
        )}

        {conclusionResult && (
          <div className="border-b border-emerald-100 bg-emerald-50 px-6 py-3 text-sm text-emerald-800">
            <div className="flex items-start justify-between gap-2">
              <div>
                <strong>Session concluded:</strong> {conclusionResult.conclusion_status}
              </div>
              <button
                onClick={() => setConclusionResult(null)}
                className="text-emerald-600 hover:text-emerald-800"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        <div ref={messagesContainerRef} className="flex-1 overflow-y-auto">
          {messages.length === 0 && !isLoading ? (
            <WelcomePanel workflow={workflow} onExampleClick={setQuery} />
          ) : (
            <div className="mx-auto max-w-3xl space-y-6 px-6 py-6">
              {messages.map((msg) => (
                <MessageBubble key={msg.id} message={msg} sessionId={activeId} />
              ))}
              {isLoading && <LoadingIndicator workflow={workflow} />}
            </div>
          )}
        </div>

        <footer className="border-t border-gray-200 bg-white px-6 py-4 shadow-[0_-4px_20px_rgba(0,0,0,0.04)]">
          <form onSubmit={handleSubmit} className="mx-auto max-w-3xl">
            {error && (
              <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">
                {error}
              </div>
            )}
            <AttachmentPreview items={attachments} onRemove={handleAttachRemove} />
            <div className="flex gap-3">
              <ImageAttachButton onSelect={handleAttachSelect} disabled={isLoading} />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Message ${branding.assistantName}…`}
                disabled={isLoading}
                className="flex-1 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm shadow-inner focus:border-brand-accent focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-accent/20"
              />
              <button
                type="submit"
                disabled={isLoading || uploading || !query.trim()}
                className="flex items-center gap-2 rounded-xl bg-brand-navy px-5 py-3 text-sm font-semibold text-white shadow-md transition hover:bg-brand-navy-light disabled:cursor-not-allowed disabled:opacity-50"
              >
                <SendIcon className="h-4 w-4" />
                Send
              </button>
            </div>
            {workflow !== "ask_agent" && (
              <p className="mt-2 text-center text-[11px] text-gray-400">{branding.disclaimer}</p>
            )}
          </form>
        </footer>

        <SessionConclusionDialog
          conversationId={activeId}
          isOpen={sessionConclusionOpen}
          onClose={() => setSessionConclusionOpen(false)}
          onSuccess={(result) => {
            setConclusionResult(result);
            setSessionConclusionOpen(false);
          }}
        />
      </div>
    </div>
  );
}
